import { Eye, EyeOff, LockIcon, MailIcon } from 'lucide-react';
import { CONTROL_SIZE } from '@/components/ui/types/control-enums';
import React, { useEffect } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { toast } from '@/components/ui/helpers/toast';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { GradientBackground } from '@/components/ui/gradient-background';
import { INPUT_APPEARANCE } from '@/components/ui/types/input-variants-enums';
import { Input } from '@/components/ui/input';
import { AUTH_CARD_TOP_OFFSET_CLASS } from '@/features/signup/constants/auth-card-constants';
import { Spinner } from '@/components/ui/spinner';
import { useTracking } from '@/hooks/analytics/use-tracking';

import { useLoginFlow } from './hooks/use-login-flow';
import { useTokenRedirect } from './hooks/use-token-redirect';

const Login: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const redirectPath = useTokenRedirect(searchParams);

  const {
    email, setEmail,
    password, setPassword,
    showPassword, setShowPassword,
    isLoading, isCheckingSubscription,
    login,
  } = useLoginFlow();

  useEffect(() => {
    if (searchParams.get('accept-invite') === '1') {
      toast.success('Account created successfully! Please log in with your credentials.');
      searchParams.delete('accept-invite');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useTracking('visitLogin');

  const attemptLogin = async (e?: React.KeyboardEvent<HTMLInputElement>) => {
    try {
      await login(e);
    } catch {
      toast.error('Login failed. Please try again.');
      return;
    }
  };

  if (redirectPath) {
    return <Navigate to={redirectPath} replace />;
  }

  const isBusy = isLoading || isCheckingSubscription;

  return (
    <GradientBackground className="min-h-[calc(100dvh-3rem)]">
      <div className="px-4 pb-12 pt-8 sm:px-6 sm:pt-10 lg:px-8 lg:pt-12">
        <div className={`mx-auto max-w-xl ${AUTH_CARD_TOP_OFFSET_CLASS}`}>
          <Card>
            <CardHeader className="space-y-1">
              <CardTitle className="text-xl">Login</CardTitle>
              <CardDescription>
                {searchParams.get('accept-invite')
                  ? 'Hi Welcome back. Please log in with your credentials.'
                  : 'Hi Welcome back'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={e => { e.preventDefault(); void attemptLogin(); }}>
                <div className="my-4">
                  <Input
                    appearance={INPUT_APPEARANCE.DEFAULT}
                    size={CONTROL_SIZE.DEFAULT}
                    required
                    id="email"
                    placeholder="Email Address"
                    name="email"
                    autoFocus
                    type="email"
                    value={email}
                    disabled={isBusy}
                    autoComplete="email"
                    onChange={e => setEmail(e.target.value)}
                    leftIcon={<MailIcon className="h-4 w-4" />}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void attemptLogin(e); } }}
                  />
                </div>
                <div className="mb-2">
                  <Input
                    appearance={INPUT_APPEARANCE.DEFAULT}
                    size={CONTROL_SIZE.DEFAULT}
                    required
                    name="password"
                    placeholder="Password"
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    disabled={isBusy}
                    autoComplete="current-password"
                    onChange={e => setPassword(e.target.value)}
                    leftIcon={<LockIcon className="h-4 w-4" />}
                    rightIcon={showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    rightOnClick={() => setShowPassword(!showPassword)}
                    rightAriaLabel={showPassword ? 'Hide password' : 'Show password'}
                    rightAriaPressed={showPassword}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void attemptLogin(e); } }}
                  />
                </div>
                <div className="flex flex-col">
                  <Link to="/forgot-password" className="text-sm text-primary">Forgot password?</Link>
                </div>
                <div className="py-8">
                  <Button type="submit" variant="default" disabled={isBusy}>
                    {isBusy && <Spinner size="small" className="ml-[2px] text-white" />}
                    {isCheckingSubscription ? 'Checking account...' : 'Submit'}
                  </Button>
                </div>
                <div className="mt-4 text-center text-sm">
                  <span className="text-content-primary">Don&apos;t have an account? </span>
                  <Link to="/signup" className="font-semibold text-primary hover:text-primary/80">Sign up</Link>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </GradientBackground>
  );
};

export default Login;
